<template>
    <div class="lx-online-booking-checkout-gallery">
        <b-row class="lx-online-booking-checkout-gallery__image-card">
            <div class="col-12">
                <div class="lx-online-booking-checkout-gallery__image-card__detail">
                    <font-awesome-icon :icon="['fas', 'bed']" />
                    <span class="ml-2">{{ beds }}</span>
                    <span class="ml-1">Bedrooms</span>
                </div>
            </div>
            <div class="col-12">
                <div class="lx-online-booking-checkout-gallery__image-card__detail">
                    <font-awesome-icon :icon="['fas', 'shower']" />
                    <span class="ml-2">{{ baths }}</span>
                    <span class="ml-1">Bathrooms</span>
                </div>
            </div>
        </b-row>
        <b-button
            v-if="showButton"
            class="lx-online-booking-checkout-gallery__show-all"
            variant="light"
            :size="isSmallScreen ? 'sm' : 'md'"
            @click="openFirstPhoto"
        >
            <font-awesome-icon :icon="['fas', 'image']"></font-awesome-icon>
            <span class="ml-1">Show all photos</span>
        </b-button>
        <photoswipe
            ref="photoswipe"
            :class="`lx-online-booking-checkout-gallery__gallery ${galleryClass}`"
            :galleryId="galleryId"
            :dataSource="dataSource"
            v-slot="{ open }"
        >
            <div
                :class="`gallery__item__photo-${i + 1}`"
                v-for="(photo, i) in galleryPhotos"
                :key="photo.id"
            >
                <a
                    :data-id="photo.id"
                    :key="photo.id"
                    :href="photo.src"
                    :data-pswp-width="photo.width"
                    :data-pswp-height="photo.height"
                    @click.prevent="open(i)"
                    target="_blank"
                    rel="noreferrer"
                >
                    <img :src="photo.src" :alt="photo.title" />
                </a>
            </div>
        </photoswipe>
    </div>
</template>

<script>
import Photoswipe from '../../../generic/photoswipe/photoswipe.vue';

export default {
    name: 'OnlineBookingCheckoutGallery',
    components: { Photoswipe },
    props: {
        galleryId: {
            type: String,
            required: true,
        },
        photos: {
            type: Array,
            required: true,
        },
        beds: {
            type: Number,
            required: true,
        },
        baths: {
            type: String,
            required: true,
        },
        galleryImageNumber: {
            type: Number,
            default: 5,
        },
    },
    computed: {
        galleryPhotos() {
            return this.photos.slice(0, this.galleryImageNumber).map((photo) => ({
                id: photo.id,
                src: photo.photo_preview,
                width: photo.width,
                height: photo.height,
            }));
        },
        galleryClass() {
            return `variant-${this.galleryPhotos.length}`;
        },
        dataSource() {
            return this.photos.map((photo) => ({
                id: photo.id,
                src: photo.photo_preview,
                width: photo.width,
                height: photo.height,
            }));
        },
        showButton() {
            return this.galleryPhotos.length > 1;
        },
        isSmallScreen() {
            const md = 768;
            return window.innerWidth < md;
        },
    },
    methods: {
        openFirstPhoto() {
            this.$refs.photoswipe.lightbox.loadAndOpen(0);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.lx-online-booking-checkout-gallery {
    position: relative;

    @include media-breakpoint-down(md) {
        margin-top: 1rem;
    }

    &__image-card{
        position: absolute;
        top: 237px !important;
        left: 16px !important;
        width: min-content;
        white-space: nowrap;
        background-color: white !important;
        z-index: 2 !important;
        padding-top: 5px;
        padding-bottom: 3px;
        border-radius: 5px;
        opacity: .95;

        @include media-breakpoint-down(sm) {
            top: 243px !important;
            font-size: 0.875rem;
            line-height: 1.5;
        }

        &__detail {
            display: flex;
            align-items: center;
            letter-spacing: 0.5px;
        }

        @include media-breakpoint-up(lg) {
            top: 241px !important;
        }
    }


    &__show-all {
        position: absolute !important;
        z-index: 1 !important;
        top: 261px !important;
        right: 1px !important;

        @include media-breakpoint-up(md) {
            top: 255px !important;
        }

        @include media-breakpoint-up(lg) {
            top: 259px !important;
        }
    }

    &__gallery {
        display: grid;
        gap: 6px;
        overflow: hidden;
        height: 300px;
        margin-top: -1.1rem;
        margin-left: -1.1rem;
        margin-right: -1.1rem;

        :deep(img) {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &.variant-1,
        &.variant-2,
        &.variant-3,
        &.variant-4,
        &.variant-5 {
            :deep(*) .gallery__item {
                &__photo-1,
                &__photo-2,
                &__photo-3,
                &__photo-4,
                &__photo-5 {
                    overflow: hidden;
                }
            }
        }

        &.variant-1 {
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: minmax(0, 1fr);

            :deep(*) .gallery__item {
                &__photo-1 {
                    grid-area: 1 / 1 / 2 / 2;
                }
            }
        }

        &.variant-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            grid-template-rows: minmax(0, 1fr);

            :deep(*) .gallery__item {
                &__photo-1 {
                    grid-area: 1 / 1 / 2 / 2;
                }
                &__photo-2 {
                    grid-area: 1 / 2 / 2 / 3;
                }
            }
        }

        &.variant-3 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            grid-template-rows: repeat(2, minmax(0, 1fr));

            :deep(*) .gallery__item {
                &__photo-1 {
                    grid-area: 1 / 1 / 3 / 2;
                }
                &__photo-2 {
                    grid-area: 1 / 2 / 2 / 3;
                }
                &__photo-3 {
                    grid-area: 2 / 2 / 3 / 3;
                }
            }
        }

        &.variant-4 {
            grid-template-columns: 1fr repeat(2, minmax(0, 0.5fr));
            grid-template-rows: repeat(2, minmax(0, 1fr));

            :deep(*) .gallery__item {
                &__photo-1 {
                    grid-area: 1 / 1 / 3 / 2;
                }
                &__photo-2 {
                    grid-area: 1 / 2 / 2 / 4;
                }
                &__photo-3 {
                    grid-area: 2 / 2 / 3 / 3;
                }
                &__photo-4 {
                    grid-area: 2 / 3 / 3 / 4;
                }
            }
        }

        &.variant-5 {
            grid-template-columns: 1fr repeat(2, minmax(0, 0.5fr));
            grid-template-rows: repeat(2, minmax(0, 1fr));

            :deep(*) .gallery__item {
                &__photo-1 {
                    grid-area: 1 / 1 / 3 / 2;
                }
                &__photo-2 {
                    grid-area: 1 / 2 / 2 / 3;
                }
                &__photo-3 {
                    grid-area: 1 / 3 / 2 / 4;
                }
                &__photo-4 {
                    grid-area: 2 / 2 / 3 / 3;
                }
                &__photo-5 {
                    grid-area: 2 / 3 / 3 / 4;
                }
            }
        }

        @include media-breakpoint-down(md) {
            &.variant-5,
            &.variant-4 {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                grid-template-rows: repeat(2, minmax(0, 1fr));

                :deep(*) .gallery__item {
                    &__photo-1 {
                        grid-area: 1 / 1 / 3 / 2;
                    }
                    &__photo-2 {
                        grid-area: 1 / 2 / 2 / 3;
                    }
                    &__photo-3 {
                        grid-area: 2 / 2 / 3 / 3;
                    }
                    &__photo-4 {
                        display: none;
                    }
                    &__photo-5 {
                        display: none;
                    }
                }
            }
        }

        @include media-breakpoint-up(xl) {
            &.variant-1 {
                :deep(img) {
                    width: auto;
                }
            }
        }

        @include media-breakpoint-down(md) {
            margin-top: -2.1rem;
        }
    }
}
</style>
